/**
 * FACE Wallet - the customer application.
 *
 * The markup and the design in index.html are the product; this file
 * makes them real. Every screen the page shows is driven by the live API:
 * the balance is the ledger balance, the Face GUID is the identity code
 * derived from the enrolment scan, the KYC result is what the document
 * assessment actually concluded, and the card number is decrypted by the
 * server only after a fresh step-up.
 *
 * The page calls global functions from `onclick` attributes, so the
 * bottom of this file publishes them on `window`. That is the contract
 * with the markup, and keeping it means the design can be edited without
 * touching this file.
 *
 * The one piece of architecture worth naming is `guard`. Any request can
 * come back saying "prove it is you first" and naming which factors it
 * will accept. Rather than every action handling that, `guard` wraps the
 * call: it runs the action, catches that one failure, runs the right
 * challenge, and retries once. Adding a new way to move money means
 * writing the action, not the security flow.
 */

import { Api, ApiFailure } from '../lib/api.ts';
import * as face from '../lib/facekit.ts';
import { qrToSvg } from '../../../packages/ui/src/qr.ts';
import { toE164, isMobile } from '../../../packages/sapay/src/banks.ts';
import { validateSaId } from '../../../packages/kyc/src/sa-id.ts';
import jsQR from 'jsqr';
import { bindInline } from '../lib/inline.ts';
import { ask, confirmDialog } from '../lib/dialog.ts';

const api = new Api('user');
const $ = (id: string) => document.getElementById(id);

/* ------------------------------- state --------------------------------- */

interface Me {
  user: { id: string; displayName: string | null; fullName: string | null; phone: string | null; email: string | null; status: string; walletLocked: boolean; lockReason: string | null; memberSince: number };
  balance: { cents: number; available: number; held: number; formatted: string };
  tier: { level: 0 | 1 | 2 | 3; name: string; status: string; canSend: boolean; canReceive: boolean; canHoldCard: boolean; canWithdraw: boolean; nextTier: { level: number; requires: string[] } | null };
  limits: { perTransaction: number; daily: number; monthly: number; balanceCeiling: number; usedToday: number; remainingToday: number };
  security: { factors: { kind: string; label: string | null }[]; stepUpAt: number | null };
  faceGuid: string | null;
  deposit: { accountNumber: string; formatted: string; branchCode: string; bankName: string; accountType: string; holderName: string; live: boolean; note: string };
}

const S = {
  me: null as Me | null,
  health: null as { rails: string; cards: string; facePepper?: string; liveness?: string; idCheck?: string } | null,
  /** Held between the face scan and the wallet being created. */
  enrolment: null as { challengeId: string; code: string; bands: string[]; liveness?: Record<string, unknown> } | null,
  signup: { name: '', phone: '', said: '' },
  card: null as CardInfo | null,
  cardRevealed: false,
  kyc: null as { caseId: string; read: face.DocumentRead } | null,
  buy: { category: '', label: '', needsTarget: 'none' as string },
  totpSetupId: '',
};

/* ------------------------------- theme --------------------------------- */

const SUN = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2.5v2.4M12 19.1v2.4M2.5 12h2.4M19.1 12h2.4M5 5l1.7 1.7M17.3 17.3 19 19M19 5l-1.7 1.7M6.7 17.3 5 19"/></svg>';
const MOON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.2 14.6A8.4 8.4 0 0 1 9.4 3.8a8.4 8.4 0 1 0 10.8 10.8z"/></svg>';

let theme: 'light' | 'dark' =
  (localStorage.getItem('fw2.theme') as 'light' | 'dark' | null) ??
  (matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');

function applyTheme(): void {
  document.documentElement.dataset.theme = theme;
  document.querySelectorAll('.themebtn').forEach((b) => { b.innerHTML = theme === 'dark' ? SUN : MOON; });
  const sw = $('darkSw') as HTMLInputElement | null;
  if (sw) sw.checked = theme === 'dark';
}
function setTheme(t: 'light' | 'dark'): void {
  theme = t;
  try { localStorage.setItem('fw2.theme', t); } catch { /* private browsing */ }
  applyTheme();
}
const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');
applyTheme();

/* ----------------------------- small helpers ---------------------------- */

function toast(message: string, ms = 3200): void {
  const t = $('toast');
  if (!t) return;
  t.textContent = message;
  t.style.display = 'block';
  const holder = t as HTMLElement & { _x?: number };
  clearTimeout(holder._x);
  holder._x = setTimeout(() => { t.style.display = 'none'; }, ms) as unknown as number;
}

/** Money in the South African convention: R1 234,56. */
function money(cents: number): string {
  const neg = cents < 0;
  const abs = Math.abs(Math.round(cents));
  const whole = Math.trunc(abs / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${neg ? '-' : ''}${whole},${(abs % 100).toString().padStart(2, '0')}`;
}

const parseAmount = (text: string): number => {
  const cleaned = String(text ?? '').replace(/[^\d.,]/g, '').replace(',', '.');
  const value = Number(cleaned);
  return Number.isFinite(value) && value > 0 ? Math.round(value * 100) : 0;
};

function go(id: string): void {
  document.querySelectorAll('.screen').forEach((s) => s.classList.remove('on'));
  $(id)?.classList.add('on');
  window.scrollTo(0, 0);
  if (id === 'sc-face') describeFaceStep();
}

/**
 * Say what the face step is actually going to do.
 *
 * The screen has no heading of its own any more, so the line under the
 * ring carries it. A page that describes a safeguard the deployment has
 * switched off is worse than one that says nothing.
 */
function describeFaceStep(): void {
  const msg = $('faceMsg');
  const sub = $('sc-face')?.querySelector('.sub');
  const text = face.livenessIsOff()
    ? 'Look into the ring and hold still. No photo is stored, only a one-way code. The liveness check is off on this deployment.'
    : 'Look into the ring and follow the prompts. No photo is stored, only a one-way code.';
  if (sub) sub.textContent = text;
  else if (msg && /^Starting camera/i.test(msg.textContent ?? '')) msg.textContent = text;
}
function tab(id: string): void {
  go(id);
  // The page reserves room for the tab bar with `body.tabs`, so the class
  // is not decoration: without it the last card sits under the bar.
  document.body.classList.add('tabs');
  const bar = $('tabbar');
  if (bar) bar.style.display = 'flex';
  document.querySelectorAll('.tab').forEach((b) => b.classList.toggle('on', (b as HTMLElement).dataset.t === id));
  if (id === 'sc-card') void loadCard();
}
/**
 * Swipe between the tabs.
 *
 * The card is one flick from the balance, the way a phone wallet is
 * expected to behave, rather than a deliberate tap on a bar at the
 * bottom. Left goes forward through the tabs, right comes back.
 *
 * Three things it deliberately will not do. It ignores a gesture that is
 * mostly vertical, so scrolling a statement never changes screen. It
 * ignores anything starting inside a sheet, a modal or the camera, where
 * a horizontal drag means something else. And it will not swipe onto the
 * card screen when there is no card to show - there is nothing there to
 * swipe to, and landing on an empty panel feels like a fault.
 */
const SWIPE_ORDER = ['sc-home', 'sc-card', 'sc-settings'];

function currentTab(): string | null {
  const on = document.querySelector('.screen.on');
  const id = on?.id ?? '';
  return SWIPE_ORDER.includes(id) ? id : null;
}

function swipeTo(direction: -1 | 1): void {
  const here = currentTab();
  if (here === null) return;
  const next = SWIPE_ORDER[SWIPE_ORDER.indexOf(here) + direction];
  if (!next) return;
  // Only onto the card screen when a card exists, as asked.
  if (next === 'sc-card' && !S.card) return;
  tab(next);
}

/**
 * Fetch the card quietly, so the swipe has somewhere to land.
 *
 * `loadCard` paints the screen and can raise a toast about the sandbox,
 * which is wrong to do while somebody is looking at their balance. This
 * only settles the question the gesture asks - is there a card? - and
 * leaves the drawing to the screen itself.
 */
async function preloadCard(): Promise<void> {
  if (!api.token) return;
  try {
    const data = await api.get<{ cards: CardInfo[] }>('/cards');
    S.card = data.cards[0] ?? null;
  } catch { /* the swipe simply stays closed until the card screen is opened */ }
}

function watchSwipes(): void {
  let x = 0;
  let y = 0;
  let at = 0;
  let live = false;

  const blocked = (target: EventTarget | null): boolean => {
    const el = target as HTMLElement | null;
    // A sheet, a modal, the face ring or the scanner: a horizontal drag
    // inside any of those is not a request to change tab.
    return !!el?.closest?.('.sheet.on, .fwdlg, #sc-face, video, canvas, input, textarea, select');
  };

  document.addEventListener('touchstart', (e) => {
    if (e.touches.length !== 1 || blocked(e.target)) { live = false; return; }
    x = e.touches[0].clientX;
    y = e.touches[0].clientY;
    at = Date.now();
    live = true;
  }, { passive: true });

  document.addEventListener('touchend', (e) => {
    if (!live) return;
    live = false;
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - x;
    const dy = t.clientY - y;
    // Far enough, quick enough, and more sideways than up or down.
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.6 || Date.now() - at > 700) return;
    swipeTo(dx < 0 ? 1 : -1);
  }, { passive: true });
}

const openSheet = (id: string) => $(id)?.classList.add('on');
const closeSheets = () => document.querySelectorAll('.sheet').forEach((s) => s.classList.remove('on'));

function copyTxt(text: string, message: string): void {
  if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(() => toast(message)).catch(() => toast(text));
  else toast(text);
}
const copyAccount = () => copyTxt(S.me?.deposit.accountNumber ?? '', 'Account number copied');
const copyBranch = () => copyTxt(S.me?.deposit.branchCode ?? '', 'Branch code copied');
function copyAllDetails(): void {
  const d = S.me?.deposit;
  if (!d) return;
  copyTxt(
    `Bank: ${d.bankName}\nAccount holder: ${d.holderName}\nAccount number: ${d.accountNumber}\nBranch code: ${d.branchCode}\nAccount type: ${d.accountType}`,
    'Bank details copied - paste into any banking app',
  );
}

const qrInto = (id: string, text: string) => {
  const host = $(id);
  if (host) host.innerHTML = qrToSvg(text, { margin: 2, dark: '#0E1A15' });
};

const failure = (e: unknown, fallback: string): string =>
  e instanceof ApiFailure ? e.message : (e as Error)?.message || fallback;

/* ----------------------------- step-up ---------------------------------- */

const FACTOR_LABEL: Record<string, string> = {
  face: 'Scan your face', passkey: 'Use this phone', totp: 'Authenticator code',
  pin: 'Enter your PIN', sms: 'Text me a code',
};

/** Run an action; if the server asks for proof, get it and try once more. */
async function guard<T>(action: (proof?: unknown) => Promise<T>): Promise<T | undefined> {
  try {
    return await action();
  } catch (e) {
    if (!(e instanceof ApiFailure) || !e.needsStepUp) throw e;
    const proof = await askForProof(e.acceptedFactors);
    if (!proof) return undefined;
    return await action(proof);
  }
}

async function askForProof(accepts: string[]): Promise<unknown | null> {
  // The enrolled face is always available; the others have to be set up.
  const have = new Set(S.me?.security.factors.map((f) => f.kind) ?? []);
  have.add('face');
  const options = accepts.filter((f) => have.has(f));

  /*
   * Nothing this action accepts has been set up.
   *
   * This used to fall back to a face scan whatever the server asked
   * for, which made revealing a card number a dead end: `card_reveal`
   * deliberately accepts only a passkey or an authenticator - a face
   * code cannot be the only thing between a compromised page and a
   * usable card - so the scan was collected, sent, and refused. Say what
   * is missing and offer the screen that adds it.
   */
  if (!options.length) {
    const names = accepts.map((f) => FACTOR_LABEL[f] ?? f);
    const go = await confirmDialog({
      title: 'This needs a stronger check',
      message: `To do this you need ${names.length > 1 ? 'one of: ' : ''}${names.join(' or ')}. Your face alone is not enough for this one. Set one up in Settings, then try again.`,
      okLabel: 'Open Settings',
      cancelLabel: 'Not now',
    });
    if (go) tab('sc-settings');
    return null;
  }

  const list = options;

  // A single accepted factor needs no menu: go straight to the challenge.
  if (list.length === 1) return collectFactor(list[0]);

  return new Promise((resolve) => {
    const panel = $('sh-choose')?.querySelector('.panel') as HTMLElement | undefined;
    if (!panel) { void collectFactor(list[0]).then(resolve); return; }
    panel.innerHTML = '<div class="grab"></div><h2>Confirm it is you</h2><p class="sm mut mt8">Choose how.</p>';
    for (const kind of list) {
      const button = document.createElement('button');
      button.className = 'btn ghost mt8';
      button.textContent = FACTOR_LABEL[kind] ?? kind;
      button.onclick = async () => {
        closeSheets();
        resolve(await collectFactor(kind));
      };
      panel.append(button);
    }
    const cancel = document.createElement('button');
    cancel.className = 'btn ghost mt8';
    cancel.textContent = 'Cancel';
    cancel.onclick = () => { closeSheets(); resolve(null); };
    panel.append(cancel);
    openSheet('sh-choose');
  });
}

async function collectFactor(kind: string): Promise<unknown | null> {
  if (kind === 'face') {
    const scan = await faceCheck();
    return scan ? { kind: 'face', faceCode: scan } : null;
  }
  if (kind === 'passkey') {
    const assertion = await passkeyAssertion();
    return assertion ? { kind: 'passkey', ...assertion } : null;
  }
  if (kind === 'sms') {
    try { await api.post('/auth/otp/send', { purpose: 'step_up' }); toast('Code sent'); }
    catch { toast('We could not send the code.'); return null; }
  }
  const code = await ask(kind === 'pin'
    ? { title: 'Enter your PIN', kind: 'number', inputMode: 'numeric', maxLength: 12, okLabel: 'Confirm' }
    : { title: 'Enter the 6-digit code', message: 'We sent it to your phone.', kind: 'number', inputMode: 'numeric', maxLength: 6, okLabel: 'Confirm' });
  if (!code) return null;
  return kind === 'pin' ? { kind: 'pin', pin: code } : { kind, code };
}

/**
 * The face check that authorises a payment.
 *
 * Uses the same movement challenge as enrolment, shortened, and reuses
 * the verification ring in the sheet the design already provides. The
 * camera is stopped on every exit path including failure: a wallet that
 * leaves the camera light on after a cancelled payment is one people stop
 * trusting, rightly.
 */
async function faceCheck(): Promise<string | null> {
  closeSheets();
  openSheet('sh-faceverify');
  const ring = $('verifyRing');
  const msg = $('verifyMsg');
  ring?.classList.remove('done');
  if (msg) msg.textContent = 'Starting the camera...';

  const wrap = ring?.parentElement as HTMLElement | undefined;
  const video = ensureVideo(wrap, 'verifyCam');
  const overlay = ensureCanvas(wrap, 'verifyOverlay');

  try {
    const outcome = await face.quickFaceCode({
      video, overlay,
      prompt: (t) => { if (msg) msg.textContent = t; },
    });
    face.closeCamera();
    if (!outcome.ok || !outcome.code) {
      if (msg) msg.textContent = outcome.reason ?? 'That did not work.';
      toast(outcome.reason ?? 'The face check did not pass.', 5000);
      setTimeout(closeSheets, 1400);
      return null;
    }
    ring?.classList.add('done');
    if (msg) msg.textContent = 'Face matched';
    await new Promise((r) => setTimeout(r, 550));
    closeSheets();
    return outcome.code;
  } catch (e) {
    face.closeCamera();
    toast(failure(e, 'The camera could not start.'), 5000);
    closeSheets();
    return null;
  }
}

function ensureVideo(wrap: HTMLElement | undefined, id: string): HTMLVideoElement {
  const existing = $(id) as HTMLVideoElement | null;
  if (existing) return existing;
  const video = document.createElement('video');
  video.id = id;
  video.playsInline = true;
  video.muted = true;
  video.autoplay = true;
  video.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;border-radius:50%;transform:scaleX(-1)';
  wrap?.append(video);
  return video;
}
function ensureCanvas(wrap: HTMLElement | undefined, id: string): HTMLCanvasElement {
  const existing = $(id) as HTMLCanvasElement | null;
  if (existing) return existing;
  const canvas = document.createElement('canvas');
  canvas.id = id;
  canvas.style.display = 'none';
  wrap?.append(canvas);
  return canvas;
}

/** The design calls this before a money action. */
function faceThen(fn: () => void | Promise<void>): void {
  void (async () => {
    closeSheets();
    await fn();
  })();
}

/* ------------------------------ passkeys -------------------------------- */

const b64urlToBytes = (s: string): Uint8Array<ArrayBuffer> => {
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};
const bytesToB64url = (b: ArrayBuffer | Uint8Array): string => {
  const arr = b instanceof Uint8Array ? b : new Uint8Array(b);
  let s = '';
  for (const x of arr) s += String.fromCharCode(x);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
const hexToBytes = (h: string): Uint8Array<ArrayBuffer> => {
  const out = new Uint8Array(new ArrayBuffer(h.length / 2));
  for (let i = 0; i < out.length; i++) out[i] = parseInt(h.substr(i * 2, 2), 16);
  return out;
};
const bytesToHex = (b: ArrayBuffer | Uint8Array): string =>
  [...(b instanceof Uint8Array ? b : new Uint8Array(b))].map((x) => x.toString(16).padStart(2, '0')).join('');

const passkeySupported = (): boolean => typeof PublicKeyCredential !== 'undefined' && !!navigator.credentials;

async function passkeyAssertion(): Promise<Record<string, unknown> | null> {
  if (!passkeySupported()) { toast('This device does not support passkeys.'); return null; }
  try {
    const opts = await api.post<{ challengeId: string; challenge: string; rpId: string; credentialIds: string[] }>('/settings/stepup/challenge');
    const credential = (await navigator.credentials.get({
      publicKey: {
        challenge: hexToBytes(opts.challenge),
        rpId: opts.rpId,
        userVerification: 'required',
        timeout: 60_000,
        allowCredentials: opts.credentialIds.map((id) => ({ id: b64urlToBytes(id), type: 'public-key' as const })),
      },
    })) as PublicKeyCredential | null;
    if (!credential) return null;
    const response = credential.response as AuthenticatorAssertionResponse;
    return {
      challengeId: opts.challengeId,
      assertion: {
        credentialId: credential.id,
        auth: bytesToHex(response.authenticatorData),
        client: bytesToHex(response.clientDataJSON),
        sig: bytesToHex(response.signature),
      },
    };
  } catch {
    toast('That was cancelled or timed out.');
    return null;
  }
}

async function addPasskey(): Promise<boolean> {
  if (!passkeySupported()) { toast('This device does not support passkeys.'); return false; }
  try {
    const opts = await api.post<{ challengeId: string; challenge: string; rpId: string; userId: string; existingCredentialIds: string[] }>('/settings/passkey/options');
    const credential = (await navigator.credentials.create({
      publicKey: {
        challenge: hexToBytes(opts.challenge),
        rp: { id: opts.rpId, name: 'FACE Wallet' },
        user: {
          id: hexToBytes([...opts.userId].map((c) => c.charCodeAt(0).toString(16).padStart(2, '0')).join('')),
          name: S.me?.user.phone ?? 'FACE wallet',
          displayName: S.me?.user.displayName ?? 'FACE wallet',
        },
        pubKeyCredParams: [{ type: 'public-key', alg: -7 }],
        // The platform authenticator with its own biometric check
        // required. A key with no user verification is "something you
        // have" only, which is not what this is for.
        authenticatorSelection: { authenticatorAttachment: 'platform', userVerification: 'required', residentKey: 'preferred' },
        excludeCredentials: opts.existingCredentialIds.map((id) => ({ id: b64urlToBytes(id), type: 'public-key' as const })),
        timeout: 60_000,
        attestation: 'none',
      },
    })) as PublicKeyCredential | null;
    if (!credential) return false;

    const spki = (credential.response as AuthenticatorAttestationResponse).getPublicKey?.();
    if (!spki) { toast('This device gave a key we cannot verify.'); return false; }
    await api.post('/settings/passkey', {
      challengeId: opts.challengeId, credentialId: credential.id,
      publicKeySpki: bytesToB64url(spki), label: 'This device',
    });
    toast('Passkey added. Payments will use it from now on.');
    await refresh();
    return true;
  } catch (e) {
    toast(failure(e, 'That was cancelled.'));
    return false;
  }
}

/* -------------------------------- boot ---------------------------------- */

async function boot(): Promise<void> {
  try {
    S.health = await api.get('/health');
    face.setFacePepper(S.health?.facePepper);
    face.setLivenessMode(S.health?.liveness);
    // The hints depend on what the deployment actually checks, and the
    // form is rendered before this resolves on a slow connection.
    refreshSignup();
    // The environment note lives in Settings and on the screens it
    // actually bears on - the card screen and the receive screen already
    // say what does and does not work - rather than as a bar across the
    // top of every page.
  } catch {
    toast('We could not reach the wallet service. Check your connection.', 6000);
  }

  const foot = $('settingsFoot');
  if (foot) {
    const live = S.health?.rails === 'live';
    foot.textContent = live
      ? 'FACE Wallet v2'
      : face.livenessIsOff()
        ? 'FACE Wallet v2 · test environment · no real money moves, and the liveness check is switched off'
        : 'FACE Wallet v2 · test environment · no real money moves';
  }

  if (!api.token) return;
  try {
    await refresh();
    tab('sc-home');
    void loadStatement();
  } catch (e) {
    if (e instanceof ApiFailure && e.status === 401) { api.token = null; return; }
    toast(failure(e, 'Could not load your wallet.'), 6000);
  }
}

async function refresh(): Promise<void> {
  S.me = await api.get<Me>('/me');
  paintHome();
}

function paintHome(): void {
  const me = S.me;
  if (!me) return;
  const d = me.deposit;

  const set = (id: string, text: string) => { const n = $(id); if (n) n.textContent = text; };

  set('homeName', me.user.fullName ?? me.user.displayName ?? 'Your wallet');
  set('balMain', money(me.balance.cents).replace('R', ''));
  set('setGuid', me.faceGuid ?? '-');
  set('acctShort', d.formatted);
  set('acctBranch', `branch ${d.branchCode}`);
  set('eftAcct', d.formatted);
  set('eftName', d.holderName);
  set('eftBank', d.bankName);
  set('eftBranch', d.branchCode);
  set('setAcct', d.formatted);
  set('setAcctBank', `${d.bankName} · branch ${d.branchCode} · ${d.accountType}`);
  set('balNote', me.balance.held > 0
    ? `${money(me.balance.held)} on hold · held in a ring-fenced trust account`
    : 'Held in a ring-fenced trust account');
  set('limitLbl', `R${money(me.limits.daily)} per day · R${money(me.limits.remainingToday)} left today`);

  const map: Record<string, [string, string]> = {
    none: ['Unverified', 'warn'], pending: ['Verification pending', 'info'],
    in_review: ['Being checked', 'info'], verified: ['Verified', 'ok'],
    rejected: ['Not verified', 'warn'], expired: ['Verification expired', 'warn'],
  };
  const [label, cls] = map[me.tier.status] ?? ['Unverified', 'warn'];
  for (const id of ['kycPill', 'setKyc']) {
    const pill = $(id);
    if (pill) { pill.textContent = label; pill.className = 'pill ' + cls; }
  }
  const nudge = $('kycNudge');
  if (nudge) nudge.style.display = me.tier.level >= 2 ? 'none' : 'block';

  // A locked wallet is told plainly, in the page, above everything else
  // on the home screen. It is the one thing that must not be missable.
  const lock = $('lockNotice');
  if (lock) {
    lock.style.display = me.user.walletLocked ? 'block' : 'none';
    if (me.user.walletLocked) {
      lock.replaceChildren();
      const title = document.createElement('h3');
      title.textContent = 'This wallet is locked';
      const body = document.createElement('p');
      body.className = 'sm mut';
      body.style.margin = '8px 0 0';
      body.textContent = 'You cannot send or receive money until it is unlocked. Please contact support.';
      lock.append(title, body);
    }
  }

  qrInto('myQr', `FW:${me.user.id}`);
  set('myWalletId', me.user.phone ?? me.user.id);

  const faceSw = $('secFace') as HTMLInputElement | null;
  if (faceSw) faceSw.checked = true;
  const smsSw = $('secSms') as HTMLInputElement | null;
  if (smsSw) smsSw.checked = me.security.factors.some((f) => f.kind === 'sms');
  const totpSw = $('sec2fa') as HTMLInputElement | null;
  if (totpSw) totpSw.checked = me.security.factors.some((f) => f.kind === 'totp');
}

const ICONS: Record<string, string> = {
  in: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 7 7 17"/><path d="M15.5 17H7V8.5"/></svg>',
  out: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17 17 7"/><path d="M8.5 7H17v8.5"/></svg>',
  shop: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9.4" cy="19.8" r="1.5"/><circle cx="17" cy="19.8" r="1.5"/><path d="M3 4h2.2l2.5 10.6h10.4L21 7.8H6.1"/></svg>',
  bolt: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2 4 13.5h6.4L9.6 22l9-11.5h-6.4L13 2z"/></svg>',
  bank: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m3 9.5 9-5.7 9 5.7"/><path d="M4.5 10v8.2M19.5 10v8.2M8.3 12.5v4.2M12 12.5v4.2M15.7 12.5v4.2"/><path d="M3 20.5h18"/></svg>',
};

interface StatementLine { id: number; at: number; amount: number; kind: string; description: string; }

async function loadStatement(): Promise<void> {
  const host = $('txnList');
  if (!host) return;
  try {
    const data = await api.get<{ lines: StatementLine[] }>('/wallet/statement?limit=40');
    if (!data.lines.length) {
      host.innerHTML = '<p class="sm mut center" style="padding:14px 0">No activity yet. Receive your first rand to get started.</p>';
      return;
    }
    host.replaceChildren();
    for (const line of data.lines) {
      const icon = line.kind.startsWith('purchase') ? 'bolt'
        : line.kind.startsWith('payment') ? 'shop'
        : line.kind.startsWith('payout') || line.kind.startsWith('deposit') ? 'bank'
        : line.amount > 0 ? 'in' : 'out';

      const row = document.createElement('div');
      row.className = 'txn';
      const dot = document.createElement('div');
      dot.className = 'dot';
      dot.innerHTML = ICONS[icon];
      const middle = document.createElement('div');
      middle.className = 'grow';
      const title = document.createElement('h3');
      // Set as text, never as markup: this string contains a payment
      // reference somebody else chose.
      title.textContent = line.description;
      const sub = document.createElement('p');
      sub.className = 'sm mut';
      sub.textContent = new Date(line.at).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
      middle.append(title, sub);
      const amount = document.createElement('div');
      amount.className = 'amt ' + (line.amount > 0 ? 'pos' : 'neg');
      amount.textContent = `${line.amount > 0 ? '+' : '-'}R${money(Math.abs(line.amount))}`;
      row.append(dot, middle, amount);
      host.append(row);
    }
  } catch {
    host.innerHTML = '<p class="sm mut center" style="padding:14px 0">Could not load your activity.</p>';
  }
}

/* ------------------------------ enrolment -------------------------------- */

/* ---------------------------- form checking ---------------------------- */

/**
 * Check the sign-up fields as they are typed.
 *
 * Both of these are checkable here, exactly, with no round trip: a South
 * African mobile number has a known shape, and an identity number carries
 * its own check digit. Letting somebody type twenty digits, walk through
 * a face scan, and only then be told the number was wrong wastes the one
 * step of the sign-up that costs real effort.
 */
function checkSignupField(which: 'name' | 'phone' | 'said'): boolean {
  const value = (($(`f-${which === 'name' ? 'name' : which}`) as HTMLInputElement)?.value ?? '').trim();
  const say = (id: string, text: string, bad: boolean) => {
    const n = $(id);
    if (!n) return;
    n.textContent = text;
    n.style.display = text ? 'block' : 'none';
    n.className = bad ? 'sm' : 'sm mut';
    n.style.color = bad ? 'var(--red)' : '';
  };

  if (which === 'name') {
    const bad = value.length > 0 && value.length < 2;
    say('err-name', bad ? 'Enter your name as it appears on your ID.' : '', bad);
    return !bad;
  }

  if (which === 'phone') {
    const digits = value.replace(/\D/g, '');
    if (!value) { say('hint-phone', '10 digits, starting 06, 07 or 08.', false); return false; }
    if (digits.length < 10) {
      say('hint-phone', `${digits.length} of 10 digits.`, false);
      return false;
    }
    const e164 = toE164(value);
    if (!e164 || !isMobile(e164)) {
      say('hint-phone', 'That is not a South African mobile number. It should start 06, 07 or 08.', true);
      return false;
    }
    say('hint-phone', `Looks right: ${e164}`, false);
    return true;
  }

  // The identity number is optional at sign-up, so an empty field is fine.
  const digits = value.replace(/\D/g, '');
  if (!digits) { say('hint-said', '13 digits. You can add this later.', false); return true; }
  if (digits.length !== 13) { say('hint-said', `${digits.length} of 13 digits.`, digits.length > 13); return false; }

  // A deployment can ask for the length only. The check digit is what
  // catches a mistyped number, so where it is not run the field says so
  // rather than implying the number was verified.
  if (S.health?.idCheck === 'length') {
    say('hint-said', '13 digits. The check digit is not verified on this deployment.', false);
    return true;
  }

  const checked = validateSaId(digits);
  if (!checked.valid) {
    say('hint-said', checked.reasons[0] ?? 'That ID number does not check out. Please retype it.', true);
    return false;
  }
  if ((checked.age ?? 0) < 18) {
    say('hint-said', 'You must be 18 or older to hold this wallet.', true);
    return false;
  }
  say('hint-said', `Born ${checked.dateOfBirth}. That checks out.`, false);
  return true;
}

/** The button is live only when the form would actually be accepted. */
function refreshSignup(): void {
  const nameOk = checkSignupField('name');
  const phoneOk = checkSignupField('phone');
  const idOk = checkSignupField('said');
  const button = $('signupNext') as HTMLButtonElement | null;
  if (button) button.disabled = !(nameOk && phoneOk && idOk);
}

function toFace(): void {
  if (!checkSignupField('phone')) { toast('Enter a South African mobile number, like 072 000 0000.'); return; }
  if (!checkSignupField('said')) { toast('Check the ID number, or leave it blank and add it later.'); return; }

  S.signup.name = ($('f-name') as HTMLInputElement)?.value.trim() ?? '';
  S.signup.phone = ($('f-phone') as HTMLInputElement)?.value.trim() ?? '';
  S.signup.said = (($('f-said') as HTMLInputElement)?.value ?? '').replace(/\D/g, '');
  go('sc-face');
  void startCam();
}

async function startCam(): Promise<void> {
  const msg = $('faceMsg');
  const button = $('faceBtn') as HTMLButtonElement | null;
  const video = $('cam') as HTMLVideoElement | null;
  const wrap = $('faceRing')?.parentElement as HTMLElement | undefined;
  if (!video || !wrap) return;

  const overlay = ensureCanvas(wrap, 'faceOverlay');
  try {
    await face.openCamera({ video, overlay, prompt: (t) => { if (msg) msg.textContent = t; } });
    video.style.display = 'block';
    const fallback = $('camFallback');
    if (fallback) fallback.style.display = 'none';
    if (msg) msg.textContent = 'Centre your face in the ring';
    if (button) button.disabled = false;
  } catch (e) {
    if (msg) msg.textContent = failure(e, 'The camera could not start. Allow camera access and reload.');
  }
}

async function captureFace(): Promise<void> {
  const button = $('faceBtn') as HTMLButtonElement | null;
  const msg = $('faceMsg');
  if (button) button.disabled = true;

  try {
    const challenge = await api.post<{ challengeId: string; sequence: string; mode?: string; notice?: string }>('/enrol/challenge');
    face.setLivenessMode(challenge.mode);
    const video = $('cam') as HTMLVideoElement;
    const overlay = ensureCanvas($('faceRing')?.parentElement as HTMLElement, 'faceOverlay');

    const outcome = await face.runChallenge(
      { video, overlay, prompt: (t) => { if (msg) msg.textContent = t; } },
      challenge.sequence,
    );

    if (!outcome.ok || !outcome.code || !outcome.bands) {
      if (msg) msg.textContent = outcome.reason ?? 'That did not work. Try again.';
      if (button) button.disabled = false;
      return;
    }

    S.enrolment = { challengeId: challenge.challengeId, code: outcome.code, bands: outcome.bands, liveness: outcome.liveness };

    // The GUID shown is the identity label derived from the face code -
    // the same value the compliance console sees. It is not a picture and
    // no picture can be rebuilt from it.
    const guid = 'FG-' + outcome.code.slice(0, 8) + '-' + outcome.code.slice(8, 16) + '-' + outcome.code.slice(16, 24);
    $('faceRing')?.classList.add('done');
    const guidText = $('guidText');
    if (guidText) guidText.textContent = guid;
    const guidCard = $('guidCard');
    if (guidCard) guidCard.style.display = 'block';
    if (msg) {
      msg.textContent = face.livenessIsOff()
        ? 'Face Key captured. No photo was stored, only an irreversible code. The liveness check is switched off on this deployment.'
        : 'Face Key registered. No photo was stored, only an irreversible code.';
    }
    const next = $('faceNext');
    if (next) next.style.display = 'block';
  } catch (e) {
    if (msg) msg.textContent = failure(e, 'That did not work.');
    if (button) button.disabled = false;
  }
}

const msgOf = (id: string) => $(id);

async function createWallet(): Promise<void> {
  if (!S.enrolment) { toast('Scan your face first.'); return; }
  const button = $('faceNext') as HTMLButtonElement | null;
  const msg = $('faceMsg');
  if (msg) msg.textContent = 'Creating your wallet...';
  if (button) { button.disabled = true; button.textContent = 'Creating...'; }

  try {
    const result = await api.post<{ token: string; phoneVerification?: { sandboxCode?: string } }>('/enrol', {
      challengeId: S.enrolment.challengeId,
      faceCode: S.enrolment.code,
      bands: S.enrolment.bands,
      liveness: S.enrolment.liveness,
      phone: S.signup.phone,
      displayName: S.signup.name,
      consent: { terms: true, privacy: true, biometric: true },
    });
    api.token = result.token;
    face.closeCamera();

    // Confirming the number does not block getting in. A tier 1 wallet
    // works without it, and putting a modal between somebody and their
    // new wallet is how a sign-up gets abandoned on the last step. In a
    // test environment the code comes back in the response, so it is
    // confirmed here and now; otherwise it waits in Settings.
    const sandboxCode = result.phoneVerification?.sandboxCode;
    if (sandboxCode) {
      try { await api.post('/auth/otp/verify', { code: sandboxCode }); }
      catch { /* the wallet works without it; Settings will offer it again */ }
    }

    if (S.signup.said) {
      // Captured at sign-up so the document check can compare against it.
      try { await api.post('/kyc/case', { targetTier: 2, fullName: S.signup.name, idNumber: S.signup.said }); }
      catch { /* they can correct it on the verification screen */ }
    }

    await refresh();
    void loadStatement();
    tab('sc-home');
    toast(`Wallet created. Welcome, ${(S.me?.user.displayName ?? S.signup.name).split(' ')[0]}.`);
  } catch (e) {
    const message = failure(e, 'We could not create the wallet.');
    // On the screen as well as in a toast: a toast at the top of a long
    // page is a message somebody scrolled past without seeing, and then
    // the button simply looks broken.
    if (msgOf('faceMsg')) msgOf('faceMsg')!.textContent = message;
    toast(message, 9000);
    if (button) { button.disabled = false; button.textContent = 'Create wallet'; }
  }
}

/** The "I already have a wallet" path: sign in with a face. */
async function demoLogin(): Promise<void> {
  go('sc-face');
  const msg = $('faceMsg');
  const button = $('faceBtn') as HTMLButtonElement | null;
  const next = $('faceNext');
  if (next) next.style.display = 'none';
  if (button) { button.style.display = 'none'; }
  if (msg) msg.textContent = 'Starting the camera...';

  const video = $('cam') as HTMLVideoElement;
  const wrap = $('faceRing')?.parentElement as HTMLElement;
  const overlay = ensureCanvas(wrap, 'faceOverlay');

  try {
    await face.openCamera({ video, overlay, prompt: (t) => { if (msg) msg.textContent = t; } });
    video.style.display = 'block';
    const fallback = $('camFallback');
    if (fallback) fallback.style.display = 'none';

    const outcome = await face.quickFaceCode({ video, overlay, prompt: (t) => { if (msg) msg.textContent = t; } });
    if (!outcome.ok || !outcome.code || !outcome.bands) {
      if (msg) msg.textContent = outcome.reason ?? 'We did not recognise you.';
      if (button) button.style.display = 'block';
      return;
    }
    const result = await api.post<{ ok: boolean; token?: string; reason?: string }>('/auth/face', {
      faceCode: outcome.code, bands: outcome.bands,
    });
    face.closeCamera();
    if (!result.ok || !result.token) {
      if (msg) msg.textContent = result.reason ?? 'We did not recognise that face.';
      if (button) button.style.display = 'block';
      return;
    }
    api.token = result.token;
    await refresh();
    void loadStatement();
    tab('sc-home');
    const firstName = (S.me?.user.displayName ?? '').split(' ')[0];
    toast(`Welcome back${firstName ? ', ' + firstName : ''}.`);
  } catch (e) {
    face.closeCamera();
    if (msg) msg.textContent = failure(e, 'Sign in failed.');
    if (button) button.style.display = 'block';
  }
}

/* --------------------------------- KYC ----------------------------------- */

const DOC_TYPES: Record<string, string> = {
  'SA Smart ID card': 'sa_id_card',
  'Green barcoded ID book': 'sa_id_book',
  'Passport': 'passport',
  'Proof of address': 'proof_of_address',
};

async function docChosen(): Promise<void> {
  const input = $('docFile') as HTMLInputElement | null;
  const file = input?.files?.[0];
  if (!file) return;

  const box = $('docBox');
  const button = $('kycBtn') as HTMLButtonElement | null;
  if (box) { box.classList.add('done'); box.textContent = `Reading ${file.name}...`; }

  try {
    await face.ensureModels();
    const docType = DOC_TYPES[($('docType') as HTMLSelectElement)?.value ?? ''] ?? 'sa_id_card';
    const read = await face.readDocument(file, { needsFace: docType !== 'proof_of_address' });

    // Say a photograph is unusable before it costs somebody their data to
    // upload it and their patience to wait for the answer.
    const problems: string[] = [];
    if (read.quality.sharpness < 60) problems.push('It looks out of focus.');
    if (read.quality.glare > 0.06) problems.push('There is glare across it.');
    if (read.quality.brightness < 45) problems.push('It is too dark.');
    if (docType !== 'proof_of_address' && read.quality.facesDetected === 0) problems.push('We could not find the photo of your face on it.');

    S.kyc = { caseId: '', read };
    if (box) box.textContent = problems.length ? `${file.name} — ${problems.join(' ')} Try again, or send it anyway.` : `Ready: ${file.name}`;
    if (button) button.disabled = false;
  } catch (e) {
    if (box) box.textContent = failure(e, 'That file could not be read. Take a photo instead.');
    if (button) button.disabled = true;
  }
}

async function runKycScan(): Promise<void> {
  if (!S.kyc) return;
  const button = $('kycBtn') as HTMLButtonElement | null;
  if (button) button.disabled = true;

  const area = $('scanArea');
  const bar = $('scanBar');
  const msg = $('scanMsg');
  const results = $('scanResults');
  if (area) area.style.display = 'block';
  if (results) results.replaceChildren();

  // The progress steps are the real stages, in order, so the bar reflects
  // what is happening rather than a fixed animation.
  const step = (width: number, text: string) => {
    if (bar) bar.style.width = width + '%';
    if (msg) msg.textContent = text;
  };

  try {
    step(15, 'Opening a verification case...');
    const said = ($('f-said') as HTMLInputElement)?.value.trim() || S.signup.said || undefined;
    const kycCase = await api.post<{ caseId: string }>('/kyc/case', {
      targetTier: 2,
      fullName: S.me?.user.fullName ?? (S.signup.name || undefined),
      idNumber: said,
    });

    step(40, 'Uploading and reading the document...');
    const docType = DOC_TYPES[($('docType') as HTMLSelectElement)?.value ?? ''] ?? 'sa_id_card';

    step(70, 'Matching the document face to your Face Key...');
    const result = await api.post<{
      verdict: 'pass' | 'refer' | 'fail';
      score: number;
      faceMatch: { verdict: string; score: number } | null;
      reasons: string[];
      extracted: { fullName?: string; dateOfBirth?: string; idNumber?: string | null; expiryDate?: string };
      caseStatus: string;
      message: string;
    }>('/kyc/document', {
      caseId: kycCase.caseId,
      docType,
      imageBase64: S.kyc.read.imageBase64,
      mime: S.kyc.read.mime,
      documentFaceCode: S.kyc.read.faceCode,
      idNumber: said,
      quality: S.kyc.read.quality,
    });

    step(100, result.verdict === 'pass' ? 'Document verified automatically' : result.verdict === 'fail' ? 'Could not verify this document' : 'Sent for a human check');
    showKycResult(result);
    await refresh();
  } catch (e) {
    step(100, 'That did not work');
    if (results) {
      const box = document.createElement('div');
      box.className = 'match';
      box.textContent = failure(e, 'The document could not be checked.');
      results.append(box);
    }
    if (button) button.disabled = false;
  }
}

function showKycResult(result: {
  verdict: 'pass' | 'refer' | 'fail';
  faceMatch: { verdict: string; score: number } | null;
  reasons: string[];
  extracted: { fullName?: string; dateOfBirth?: string; idNumber?: string | null; expiryDate?: string };
  caseStatus: string;
  message: string;
}): void {
  const host = $('scanResults');
  if (!host) return;
  host.replaceChildren();

  const row = (label: string, value: string, pillClass?: string) => {
    const line = document.createElement('div');
    line.className = 'match';
    const left = document.createElement('span');
    left.className = 'mut';
    left.textContent = label;
    const right = document.createElement('span');
    right.className = pillClass ? 'pill ' + pillClass : 'b';
    right.textContent = value;
    line.append(left, right);
    host.append(line);
  };

  const e = result.extracted;
  if (e.fullName) row('Name read', e.fullName);
  if (e.idNumber) row('ID number read', e.idNumber);
  if (e.dateOfBirth) row('Date of birth', e.dateOfBirth);
  if (e.expiryDate) row('Expires', e.expiryDate);

  if (result.faceMatch) {
    row(
      'Face on document vs your Face Key',
      `${result.faceMatch.score} / 100`,
      result.faceMatch.verdict === 'match' ? 'ok' : result.faceMatch.verdict === 'review' ? 'info' : 'warn',
    );
  }
  row('Result', result.verdict === 'pass' ? 'Verified' : result.verdict === 'refer' ? 'Being checked' : 'Not verified',
      result.verdict === 'pass' ? 'ok' : result.verdict === 'refer' ? 'info' : 'warn');

  if (result.reasons.length) {
    const list = document.createElement('ul');
    list.className = 'reasons';
    for (const reason of result.reasons) {
      const item = document.createElement('li');
      item.textContent = reason;
      list.append(item);
    }
    host.append(list);
  }

  const done = $('kycDone');
  if (done) {
    done.style.display = 'block';
    done.textContent = result.verdict === 'pass' ? 'Finish - wallet secured' : 'Back to my wallet';
  }
  toast(result.message, 7000);
}

async function finishKyc(): Promise<void> {
  await refresh();
  void loadStatement();
  tab('sc-home');
}

/* -------------------------------- sending -------------------------------- */

let recipient: { userId: string; displayName: string } | null = null;

function sendTypeChange(): void {
  const kind = ($('sendType') as HTMLSelectElement)?.value ?? 'fw';
  const show = (id: string, on: boolean) => { const el = $(id); if (el) el.style.display = on ? 'block' : 'none'; };
  show('sendFw', kind === 'fw');
  show('sendQr', kind === 'qr');
  show('sendBank', kind === 'bank');
  const noteEl = $('sendFeeNote');
  if (noteEl) noteEl.textContent = kind === 'bank'
    ? 'The rail and the fee are chosen for you and shown before you confirm.'
    : 'Wallet-to-wallet transfers are free';
}

/**
 * Send to the code on somebody's Receive screen.
 *
 * Their code names them and nothing else - no amount, no reference - so
 * this hands off to the same scanner the till codes use and comes back
 * with a person to pay.
 */
/**
 * Turn the balance panel over to show the card.
 *
 * Double-tap the panel's background and it rotates, the way a physical
 * card does in the hand. The four action buttons sit outside the
 * rotating part, so they stay put and stay usable throughout - turning
 * them over with the rest would mean the most-used controls on the
 * screen vanish for half a second every time somebody wants to read
 * their card number.
 *
 * Only the last four digits are shown here. The full number lives behind
 * a fresh passkey check on the card screen, and a panel anybody can flip
 * by tapping twice is not the place to put it.
 */
function flipHero(): void {
  const flip = $('heroFlip');
  if (!flip) return;
  const showing = flip.classList.contains('flipped');
  if (!showing && !S.card) {
    toast(S.me?.tier.canHoldCard ? 'No card yet. Add one on the Card tab.' : 'Verify your identity to add a card.', 5000);
    return;
  }
  if (!showing) paintMiniCard();
  // Flipping away hides anything revealed: the number must not still be
  // sitting on the back of the panel when it is turned over again.
  if (showing && miniRevealed) hideMiniCard();
  flip.classList.toggle('flipped');
  // The face that is turned away should not be read out or reachable by
  // a keyboard while it is behind the other one.
  const back = $('heroCard');
  if (back) back.setAttribute('aria-hidden', showing ? 'true' : 'false');
}

/**
 * Show the full number on the flipped panel, behind a fresh check.
 *
 * The flip itself is deliberately cheap - two taps, no ceremony - so it
 * shows nothing that is worth stealing. The number, the CVV and the
 * expiry together are the card, so revealing them takes the same
 * step-up as the Card tab: a fresh passkey or face, checked by the
 * server, not a flag held in the page.
 *
 * It hides itself again on the deadline the server sets. A card number
 * left on a screen is a card number somebody else photographs.
 */
const ICON_EYE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12z"/><circle cx="12" cy="12" r="2.8"/></svg>';
const ICON_EYE_OFF = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3l18 18"/><path d="M10.6 6.1A9.7 9.7 0 0 1 12 6c6.5 0 10 6 10 6a17 17 0 0 1-3.2 3.9"/><path d="M6.2 8.1A17 17 0 0 0 2 12s3.5 6 10 6a9.6 9.6 0 0 0 3.6-.7"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/></svg>';

let miniRevealed: { number: string; cvv: string; expiry: string } | null = null;
let miniHideTimer = 0;

async function revealMiniCard(): Promise<void> {
  if (!S.card) return;
  if (miniRevealed) { hideMiniCard(); return; }
  try {
    const details = await guard((proof) => api.post<{ number: string; cvv: string; expiry: string; hideAfterSeconds: number }>(
      `/cards/${S.card?.id}/reveal`, { stepUp: proof },
    ));
    if (!details) return;
    miniRevealed = { number: details.number, cvv: details.cvv, expiry: details.expiry };
    paintMiniCard();
    clearTimeout(miniHideTimer);
    miniHideTimer = setTimeout(hideMiniCard, details.hideAfterSeconds * 1000) as unknown as number;
    toast(`Hidden again in ${details.hideAfterSeconds} seconds.`, 4000);
  } catch (e) {
    toast(failure(e, 'Could not show the number.'), 7000);
  }
}

function hideMiniCard(): void {
  clearTimeout(miniHideTimer);
  miniRevealed = null;
  paintMiniCard();
}

/**
 * The four fields a checkout asks for, in one go.
 *
 * Copying reveals first if it has to, so the button works on the first
 * press rather than telling somebody to press a different one first.
 */
async function copyCardDetails(): Promise<void> {
  if (!S.card) return;
  if (!miniRevealed) {
    await revealMiniCard();
    if (!miniRevealed) return;
  }
  const name = S.card.cardholderName || (S.me?.user.displayName ?? '');
  copyTxt(
    [`Card number: ${miniRevealed.number}`, `Expires: ${miniRevealed.expiry}`, `CVV: ${miniRevealed.cvv}`, `Name: ${name}`].join('\n'),
    'Copied',
  );
}

function paintMiniCard(): void {
  const card = S.card;
  if (!card) return;
  const set = (id: string, text: string) => { const n = $(id); if (n) n.textContent = text; };
  const shown = miniRevealed;
  set('mcNum', shown
    ? shown.number.replace(/(.{4})/g, '$1 ').trim()
    : `\u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 ${card.last4}`);
  $('mcNum')?.classList.toggle('shown', !!shown);
  set('mcName', card.cardholderName || (S.me?.user.displayName ?? ''));
  set('mcExp', shown?.expiry || card.expiry || '--/--');
  set('mcCvv', shown?.cvv ?? '---');
  const cvvBox = $('mcCvvBox');
  if (cvvBox) cvvBox.style.display = shown ? 'block' : 'none';
  // Icons, never text. Writing textContent here wiped the SVG out of the
  // button and left the word overflowing a 21-pixel circle.
  const reveal = $('mcReveal');
  if (reveal) {
    reveal.innerHTML = shown ? ICON_EYE_OFF : ICON_EYE;
    const label = shown ? 'Hide the card details' : 'Reveal the card details';
    reveal.setAttribute('aria-label', label);
    reveal.setAttribute('title', shown ? 'Hide' : 'Reveal');
  }
}

/**
 * Listen for two taps on the panel, but not on its buttons.
 *
 * `dblclick` alone is unreliable on a phone, so a pair of quick taps
 * close together is recognised directly. Taps that land on Receive,
 * Send, Scan or Buy are left alone: those already do something, and
 * pressing one twice in a hurry should not also turn the panel over.
 */
function watchHeroFlip(): void {
  const hero = document.querySelector('#sc-home .hero') as HTMLElement | null;
  if (!hero) return;
  const onButton = (t: EventTarget | null) => !!(t as HTMLElement | null)?.closest?.('.quick, button, a, input');

  hero.addEventListener('dblclick', (e) => {
    if (onButton(e.target)) return;
    flipHero();
  });

  let lastAt = 0;
  let lastX = 0;
  let lastY = 0;
  hero.addEventListener('touchend', (e) => {
    if (onButton(e.target)) return;
    const t = e.changedTouches[0];
    if (!t) return;
    const now = Date.now();
    const near = Math.abs(t.clientX - lastX) < 40 && Math.abs(t.clientY - lastY) < 40;
    if (now - lastAt < 320 && near) {
      e.preventDefault();
      lastAt = 0;
      flipHero();
      return;
    }
    lastAt = now; lastX = t.clientX; lastY = t.clientY;
  });
}

function scanToSend(): void {
  closeSheets();
  // The scan sheet starts its own camera when it becomes visible, the
  // same way the Scan button on the home screen reaches it.
  openSheet('sh-spend');
}

async function lookupRecipient(): Promise<void> {
  const q = ($('sendTo') as HTMLInputElement)?.value.trim() ?? '';
  const found = $('sendToFound');
  recipient = null;
  if (!found) return;
  if (q.length < 6) { found.textContent = ''; return; }
  try {
    const r = await api.get<{ found: boolean; userId?: string; displayName?: string; canReceive?: boolean; reason?: string }>(
      `/wallet/recipient?q=${encodeURIComponent(q)}`,
    );
    if (!r.found) { found.textContent = r.reason ?? 'No wallet found for that number.'; return; }
    recipient = { userId: r.userId as string, displayName: r.displayName as string };
    found.textContent = r.canReceive ? `Paying ${r.displayName}` : `${r.displayName} cannot receive money right now`;
  } catch {
    found.textContent = '';
  }
}

async function doSend(): Promise<void> {
  const amount = parseAmount(($('sendAmt') as HTMLInputElement)?.value ?? '');
  if (!amount) { toast('Enter an amount to send.'); return; }

  const toBank = ($('sendType') as HTMLSelectElement)?.value === 'bank';
  const noteText = ($('sendNote') as HTMLInputElement)?.value.trim() || undefined;

  try {
    if (toBank) {
      const select = $('bankName') as HTMLSelectElement;
      const branchCode = select?.selectedOptions[0]?.dataset.bc ?? '';
      const accountNumber = (($('bankAcct') as HTMLInputElement)?.value ?? '').replace(/\D/g, '');
      if (!accountNumber) { toast('Enter the account number.'); return; }

      // The account is added and verified before anything is sent, so a
      // mistyped number is caught before the money leaves rather than
      // after it has gone somewhere else.
      const added = await guard((proof) => api.post<{ id: string }>('/bank-accounts', {
        branchCode, accountNumber, accountType: 'cheque', stepUp: proof,
      }));
      if (!added) return;

      const quote = await api.get<{ railName: string; eta: string; feeFormatted: string }>(
        `/payouts/quote?amountCents=${amount}&bankAccountId=${added.id}`,
      );
      const go = await confirmDialog({
        title: `Send R${money(amount)} by ${quote.railName}?`,
        message: `${quote.eta}. Fee ${quote.feeFormatted}.`,
        okLabel: 'Send',
      });
      if (!go) return;

      const result = await guard((proof) => api.post<{ formatted: string; eta: string; sandboxNotice?: string }>('/payouts', {
        amountCents: amount, bankAccountId: added.id, reference: noteText,
        idempotencyKey: `payout:${crypto.randomUUID()}`, stepUp: proof,
      }));
      if (!result) return;
      toast(`${result.formatted} on its way. ${result.eta}.`, 7000);
      if (result.sandboxNotice) setTimeout(() => toast(result.sandboxNotice as string, 9000), 500);
    } else {
      if (!recipient) { await lookupRecipient(); }
      if (!recipient) { toast('Enter the number of the person you are paying.'); return; }

      const result = await guard((proof) => api.post<{ formatted: string; balanceAfterFormatted: string; to: { displayName: string } }>('/wallet/transfer', {
        to: recipient?.userId, amountCents: amount, note: noteText,
        idempotencyKey: `send:${crypto.randomUUID()}`, stepUp: proof,
      }));
      if (!result) return;
      toast(`Sent ${result.formatted} to ${result.to.displayName}.`, 6000);
    }

    closeSheets();
    ($('sendAmt') as HTMLInputElement).value = '';
    ($('sendNote') as HTMLInputElement).value = '';
    await refresh();
    void loadStatement();
  } catch (e) {
    toast(failure(e, 'The payment did not go through.'), 7000);
  }
}

/* ------------------------------ scan and pay ------------------------------ */

let scanStop: (() => void) | null = null;
let pendingCharge: { chargeId: string | null; payload: string; merchant: string; payeeId: string | null; amountCents: number | null; editable: boolean } | null = null;

function startScanner(): void {
  const video = $('scanCam') as HTMLVideoElement | null;
  const status = $('scanStatus');
  if (!video) return;

  const canvas = document.createElement('canvas');
  let stopped = false;
  let stream: MediaStream | null = null;
  scanStop = () => { stopped = true; stream?.getTracks().forEach((t) => t.stop()); scanStop = null; };

  void (async () => {
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment', width: 1280, height: 720 } });
      video.srcObject = stream;
      await video.play();
      if (status) status.textContent = 'Point at the code';
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      const tick = () => {
        if (stopped) return;
        if (video.readyState >= 2 && ctx) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0);
          const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const found = jsQR(image.data, image.width, image.height, { inversionAttempts: 'dontInvert' });
          if (found?.data) { scanStop?.(); void inspectCode(found.data); return; }
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    } catch {
      if (status) status.textContent = 'The camera could not start. Type the code instead.';
    }
  })();
}

async function inspectCode(payload: string): Promise<void> {
  const status = $('scanStatus');
  try {
    const info = await api.post<{
      ok: boolean; reason?: string; kind?: 'person' | 'merchant'; chargeId: string | null;
      person: { id: string; name: string } | null;
      merchant: { id: string; name: string } | null; amountCents: number | null;
      amountEditable: boolean; description: string | null;
    }>('/wallet/qr/inspect', { payload });

    if (!info.ok) {
      if (status) status.textContent = info.reason ?? 'That code could not be read.';
      toast(info.reason ?? 'That code could not be read.', 6000);
      return;
    }

    const person = info.kind === 'person' ? info.person : null;
    pendingCharge = {
      chargeId: info.chargeId, payload,
      merchant: person?.name ?? info.merchant?.name ?? '',
      payeeId: person?.id ?? null,
      amountCents: info.amountCents, editable: info.amountEditable,
    };

    const confirmArea = $('spendConfirm');
    if (confirmArea) confirmArea.style.display = 'block';
    const merchantEl = $('spendMerchant');
    if (merchantEl) merchantEl.textContent = pendingCharge.merchant;
    const descEl = $('spendDesc');
    if (descEl) descEl.textContent = person ? 'A FaceWallet wallet - free and instant' : (info.description ?? '');
    const amountInput = $('spendAmt') as HTMLInputElement | null;
    if (amountInput) {
      amountInput.value = info.amountCents ? (info.amountCents / 100).toFixed(2) : '';
      amountInput.readOnly = !info.amountEditable;
    }
    const button = $('spendBtn');
    if (button) {
      const verb = person ? 'send' : 'pay';
      button.textContent = `Verify face & ${verb} ${info.amountCents ? 'R' + money(info.amountCents) : ''}`.trim();
    }
    if (status) status.textContent = 'Code read';
  } catch (e) {
    toast(failure(e, 'That code could not be read.'), 6000);
  }
}

async function doSpend(): Promise<void> {
  if (!pendingCharge) {
    const typed = ($('spendCode') as HTMLInputElement)?.value.trim();
    if (typed) { await inspectCode(typed); return; }
    toast('Scan a code or paste one.');
    return;
  }

  const amount = pendingCharge.amountCents ?? parseAmount(($('spendAmt') as HTMLInputElement)?.value ?? '');
  if (!amount) { toast('Enter the amount to pay.'); return; }

  const payeeId = pendingCharge.payeeId;
  try {
    if (payeeId) {
      // A person's code, so this is a wallet-to-wallet transfer on the
      // same rails as sending to a mobile number.
      const sent = await guard((proof) => api.post<{ formatted: string; to: { name?: string } | null }>('/wallet/transfer', {
        to: payeeId, amountCents: amount,
        idempotencyKey: `qr:${crypto.randomUUID()}`,
        stepUp: proof,
      }));
      if (!sent) return;
      closeSheets();
      resetScanSheet();
      await refresh();
      void loadStatement();
      toast(`Sent ${sent.formatted} to ${pendingCharge?.merchant || 'them'}.`, 6000);
      return;
    }

    const result = await guard((proof) => api.post<{ formatted: string; merchant: { name: string } }>('/wallet/qr/pay', {
      payload: pendingCharge?.payload,
      chargeId: pendingCharge?.chargeId,
      amountCents: amount,
      idempotencyKey: pendingCharge?.chargeId ? undefined : `qr:${crypto.randomUUID()}`,
      stepUp: proof,
    }));
    if (!result) return;
    closeSheets();
    resetScanSheet();
    await refresh();
    void loadStatement();
    toast(`Paid ${result.formatted} to ${result.merchant.name}.`, 6000);
  } catch (e) {
    toast(failure(e, 'The payment did not go through.'), 7000);
  }
}

function resetScanSheet(): void {
  scanStop?.();
  pendingCharge = null;
  const confirmArea = $('spendConfirm');
  if (confirmArea) confirmArea.style.display = 'none';
  const button = $('spendBtn');
  if (button) button.textContent = 'Read the code';
  const code = $('spendCode') as HTMLInputElement | null;
  if (code) code.value = '';
}

/* -------------------------------- buying ---------------------------------- */

const BUY_CATEGORIES: Record<string, { category: string; needsTarget: string; targetLabel: string }> = {
  Airtime: { category: 'airtime', needsTarget: 'msisdn', targetLabel: 'Mobile number' },
  Electricity: { category: 'electricity', needsTarget: 'meter', targetLabel: 'Meter number' },
  Water: { category: 'water', needsTarget: 'meter', targetLabel: 'Meter number' },
  DStv: { category: 'tv', needsTarget: 'account', targetLabel: 'Account number' },
};

function buyPick(what: string): void {
  const spec = BUY_CATEGORIES[what];
  if (!spec) return;
  S.buy = { category: spec.category, label: what, needsTarget: spec.needsTarget };

  const show = (id: string, display = 'block') => { const n = $(id); if (n) n.style.display = display; };
  show('buyTargetLbl'); show('buyTarget'); show('buyLbl'); show('buyAmt'); show('buyBtn');

  const targetLabel = $('buyTargetLbl');
  if (targetLabel) targetLabel.textContent = spec.targetLabel;
  const target = $('buyTarget') as HTMLInputElement | null;
  if (target) {
    target.placeholder = spec.needsTarget === 'msisdn' ? '072 000 0000' : spec.targetLabel;
    if (spec.needsTarget === 'msisdn' && S.me?.user.phone) target.value = S.me.user.phone;
  }
  const amountLabel = $('buyLbl');
  if (amountLabel) amountLabel.textContent = `${what} amount (R)`;
  ($('buyAmt') as HTMLInputElement | null)?.focus();
}

async function doBuy(): Promise<void> {
  const amount = parseAmount(($('buyAmt') as HTMLInputElement)?.value ?? '');
  if (!amount) { toast('Enter an amount.'); return; }
  const target = ($('buyTarget') as HTMLInputElement)?.value.trim() ?? '';

  try {
    const result = await guard((proof) => api.post<{ message: string }>('/purchases', {
      category: S.buy.category, target, amountCents: amount, stepUp: proof,
      idempotencyKey: `buy:${crypto.randomUUID()}`,
    }));
    if (!result) return;
    closeSheets();
    ($('buyAmt') as HTMLInputElement).value = '';
    await refresh();
    void loadStatement();
    toast(result.message, 8000);
  } catch (e) {
    toast(failure(e, 'That purchase did not go through.'), 7000);
  }
}

/* --------------------------------- card ----------------------------------- */

interface CardInfo {
  id: string; maskedNumber: string; last4: string; expiry: string; cardholderName: string;
  status: string; frozenReason: string | null;
  controls: {
    online: boolean; contactless: boolean; international: boolean; atm: boolean;
    perTransactionCents: number; dailyCents: number; monthlyCents: number;
  };
}

interface CardTxn { id: string; at: number; amount: number; status: string }

async function loadCard(): Promise<void> {
  try {
    const data = await api.get<{ cards: CardInfo[]; sandboxNotice?: string }>('/cards');
    const none = $('noCard');
    const has = $('hasCard');
    if (!data.cards.length) {
      if (none) none.style.display = 'block';
      if (has) has.style.display = 'none';
      S.card = null;
      paintCardGate();
      return;
    }
    S.card = data.cards[0];
    if (none) none.style.display = 'none';
    if (has) has.style.display = 'block';
    paintCard();
    void renderCardLimits();
    if (data.sandboxNotice) toast(data.sandboxNotice, 9000);
  } catch (e) {
    toast(failure(e, 'Could not load your card.'));
  }
}

function paintCard(): void {
  const card = S.card;
  if (!card) return;
  const set = (id: string, text: string) => { const n = $(id); if (n) n.textContent = text; };

  set('cardNum', S.cardRevealed ? (($('cardNum') as HTMLElement).textContent ?? '') : `**** **** **** ${card.last4}`);
  set('cardName', card.cardholderName);
  set('cardExp', card.expiry);
  if (!S.cardRevealed) set('cardCvv', '***');

  const frozen = card.status !== 'active';
  $('vcard')?.classList.toggle('frozen', frozen);
  set('freezeBtn', frozen ? 'Unfreeze card' : 'Freeze card');
  set('revealBtn', S.cardRevealed ? 'Hide details' : 'Reveal details');

  const toggle = (id: string, value: boolean) => { const n = $(id) as HTMLInputElement | null; if (n) n.checked = value; };
  toggle('ctlOnline', card.controls.online);
  toggle('ctlTap', card.controls.contactless);
  toggle('ctlIntl', card.controls.international);
}

/**
 * The spending limits panel.
 *
 * The bar shows what has actually been spent on this card this calendar
 * month, taken from its own authorisations rather than from the wallet's
 * total, because the card limit is a limit on the card.
 */
async function renderCardLimits(): Promise<void> {
  const card = S.card;
  if (!card) return;
  const set = (id: string, text: string) => { const n = $(id); if (n) n.textContent = text; };

  set('cardMoLbl', `${money(card.controls.monthlyCents)} per month`);
  set('cardTxLbl', `${money(card.controls.perTransactionCents)} per purchase`);

  let spent = 0;
  try {
    const data = await api.get<{ transactions: CardTxn[] }>(`/cards/${card.id}/transactions`);
    const monthStart = new Date();
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);
    spent = data.transactions
      .filter((t) => t.at >= monthStart.getTime() && ['approved', 'captured', 'partially_captured'].includes(t.status))
      .reduce((a, b) => a + b.amount, 0);
  } catch { /* the bar simply shows nothing used */ }

  const pct = card.controls.monthlyCents > 0
    ? Math.min(100, Math.round((spent / card.controls.monthlyCents) * 100))
    : 0;
  set('cardMoUsed', `${money(spent)} used (${pct}%) - resets on the 1st`);
  const bar = $('cardMoBar');
  if (bar) {
    bar.style.width = pct + '%';
    bar.style.background = pct >= 100 ? 'var(--red)' : pct >= 80 ? '#F59E0B' : 'var(--gold)';
  }
}

/**
 * Change a card limit.
 *
 * Lowering one is instant: making your own wallet stricter should never
 * be gated. Raising one is a security change, so it goes through the
 * same step-up as everything else - and the server clamps it to what the
 * verification tier allows, whatever is typed here.
 */
async function changeCardLimit(kind: 'monthly' | 'perTx'): Promise<void> {
  const card = S.card;
  if (!card) return;
  const current = kind === 'monthly' ? card.controls.monthlyCents : card.controls.perTransactionCents;
  const typed = await ask({
    title: kind === 'monthly' ? 'New monthly card limit' : 'New per-purchase limit',
    message: 'In rand. Raising a limit needs a face check; lowering it is instant.',
    kind: 'number', inputMode: 'decimal',
    value: String(Math.round(current / 100)),
    okLabel: 'Set limit',
  });
  if (typed === null) return;

  const cents = parseAmount(typed);
  if (!cents) { toast('Enter an amount.'); return; }

  try {
    const patch = kind === 'monthly' ? { limitMonthlyCents: cents } : { limitPerTransactionCents: cents };
    const updated = await guard((proof) => api.patch<{ card: CardInfo }>(`/cards/${card.id}`, { ...patch, stepUp: proof }));
    if (!updated) return;
    S.card = updated.card;
    paintCard();
    await renderCardLimits();
    const applied = kind === 'monthly' ? updated.card.controls.monthlyCents : updated.card.controls.perTransactionCents;
    toast(applied < cents
      ? `Set to ${money(applied)}, which is the most your verification tier allows.`
      : `${kind === 'monthly' ? 'Monthly' : 'Per-purchase'} limit set to ${money(applied)}.`, 6000);
  } catch (e) {
    toast(failure(e, 'Could not change the limit.'), 6000);
  }
}

/**
 * Say what the button will actually do.
 *
 * A wallet below the verified tier cannot hold a card, and the server is
 * right to refuse - but the panel was offering "Add virtual Visa card"
 * regardless, so pressing it produced the same refusal every time with
 * no way forward. The button now names the step that is actually
 * missing, and takes the person to it.
 */
function paintCardGate(): void {
  const title = $('noCardTitle');
  const text = $('noCardText');
  const btn = $('noCardBtn');
  const allowed = S.me?.tier.canHoldCard ?? true;
  if (title) title.textContent = allowed ? 'No card yet' : 'Verify your identity first';
  if (text) {
    text.textContent = allowed
      ? 'Add a free virtual Visa linked to your ZAR balance. Issued instantly - no plastic, nothing to lose or skim.'
      : 'A virtual Visa needs a verified identity. Photograph your ID and take a face scan - it takes about a minute, and it raises your limits too.';
  }
  if (btn) btn.textContent = allowed ? 'Add virtual Visa card' : 'Verify my identity';
}

async function addCard(): Promise<void> {
  // Pressing it below the verified tier goes to the step that is
  // missing, rather than asking the server for a refusal.
  if (S.me && !S.me.tier.canHoldCard) {
    go('sc-kyc');
    return;
  }
  try {
    await guard((proof) => api.post('/cards', { stepUp: proof }));
    await loadCard();
    toast('Virtual Visa issued.');
  } catch (e) {
    toast(failure(e, 'Could not add a card.'), 7000);
  }
}

async function toggleCardDetails(): Promise<void> {
  if (!S.card) return;
  if (S.cardRevealed) {
    S.cardRevealed = false;
    paintCard();
    return;
  }
  try {
    const details = await guard((proof) => api.post<{ number: string; cvv: string; expiry: string; hideAfterSeconds: number }>(
      `/cards/${S.card?.id}/reveal`, { stepUp: proof },
    ));
    if (!details) return;
    S.cardRevealed = true;
    const num = $('cardNum');
    if (num) num.textContent = details.number.replace(/(.{4})/g, '$1 ').trim();
    const cvv = $('cardCvv');
    if (cvv) cvv.textContent = details.cvv;
    const reveal = $('revealBtn');
    if (reveal) reveal.textContent = 'Hide details';
    // Hidden again automatically: a card number left on screen is a card
    // number somebody else photographs.
    setTimeout(() => { if (S.cardRevealed) { S.cardRevealed = false; paintCard(); } }, details.hideAfterSeconds * 1000);
  } catch (e) {
    toast(failure(e, 'Could not show the number.'), 7000);
  }
}

async function toggleFreeze(): Promise<void> {
  if (!S.card) return;
  const freezing = S.card.status === 'active';
  try {
    await api.patch(`/cards/${S.card.id}`, { freeze: freezing });
    await loadCard();
    toast(freezing ? 'Card frozen. Nothing can be charged to it.' : 'Card active again.');
  } catch (e) {
    toast(failure(e, 'Could not change the card.'));
  }
}

async function saveCardControls(): Promise<void> {
  if (!S.card) return;
  const read = (id: string) => ($(id) as HTMLInputElement | null)?.checked ?? false;
  try {
    await guard(() => api.patch(`/cards/${S.card?.id}`, {
      allowOnline: read('ctlOnline'),
      allowContactless: read('ctlTap'),
      allowInternational: read('ctlIntl'),
    }));
    toast('Card settings saved.');
  } catch (e) {
    toast(failure(e, 'Could not save that.'));
    await loadCard();
  }
}

/* ------------------------------- settings ---------------------------------- */

async function secChange(name: string, input: HTMLInputElement): Promise<void> {
  // The face is always available and cannot be switched off: it is the
  // fallback that stops somebody locking themselves out entirely.
  if (name === 'Face verification') {
    input.checked = true;
    toast('The face check is always on. It is what recovers your wallet on a new phone.');
    return;
  }
  if (name === 'SMS OTP') {
    if (input.checked) {
      try {
        const r = await api.post<{ sandboxCode?: string }>('/auth/otp/send', { purpose: 'verify_phone' });
        const code = await ask({
          title: 'Enter the code we sent you',
          message: r.sandboxCode ? `Test environment, so it is ${r.sandboxCode}.` : undefined,
          kind: 'number', inputMode: 'numeric', maxLength: 6,
          value: r.sandboxCode ?? '',
          okLabel: 'Confirm',
        });
        if (!code) { input.checked = false; return; }
        await api.post('/auth/otp/verify', { code });
        await refresh();
        toast('Text-message codes turned on.');
      } catch (e) {
        input.checked = false;
        toast(failure(e, 'That did not work.'));
      }
    } else {
      toast('Leave this on unless you have an authenticator app or a passkey.');
      input.checked = true;
    }
    return;
  }
  toast(`${name} ${input.checked ? 'turned on' : 'turned off'}.`);
}

async function setup2fa(input: HTMLInputElement): Promise<void> {
  if (!input.checked) { toast('Remove an authenticator app in Security settings.'); input.checked = true; return; }
  input.checked = false;
  try {
    const setup = await api.post<{ setupId: string; secret: string; uri: string }>('/settings/totp/begin');
    S.totpSetupId = setup.setupId;
    qrInto('qr2fa', setup.uri);
    const secret = $('totpSecret');
    if (secret) secret.textContent = setup.secret;
    openSheet('sh-2fa');
  } catch (e) {
    toast(failure(e, 'Could not start the setup.'));
  }
}

async function confirm2fa(): Promise<void> {
  const code = ($('code2fa') as HTMLInputElement)?.value.trim() ?? '';
  if (code.length !== 6) { toast('Enter the six digits from your authenticator app.'); return; }
  try {
    await api.post('/settings/totp/confirm', { setupId: S.totpSetupId, code });
    const sw = $('sec2fa') as HTMLInputElement | null;
    if (sw) sw.checked = true;
    closeSheets();
    await refresh();
    toast('Authenticator app is on.');
  } catch (e) {
    toast(failure(e, 'That code is not right. Check your phone clock and try the next one.'));
  }
}

async function changeLimit(): Promise<void> {
  const current = S.me?.limits.daily ?? 0;
  const value = await ask({
    title: 'Set your own daily limit',
    message: 'In rand. You can only make it stricter than your tier allows.',
    kind: 'number', inputMode: 'decimal',
    value: String(Math.round(current / 100)),
    okLabel: 'Set limit',
  });
  if (!value) return;
  const cents = parseAmount(value);
  if (!cents) { toast('Enter an amount.'); return; }
  try {
    const result = await guard((proof) => api.post<{ dailyCents: number; clamped: boolean; message?: string }>('/settings/spend-limit', {
      dailyCents: cents, stepUp: proof,
    }));
    if (!result) return;
    await refresh();
    toast(result.message ?? `Daily limit set to R${money(result.dailyCents)}.`, 6000);
  } catch (e) {
    toast(failure(e, 'Could not change the limit.'));
  }
}

/* ------------------------------- wiring ------------------------------------ */

/** Open a file chooser the markup points at by id. */
function pickFile(id: string): void {
  ($(id) as HTMLInputElement | null)?.click();
}

const globals: Record<string, unknown> = {
  pickFile,
  applyTheme, setTheme, toggleTheme,
  go, tab, openSheet, closeSheets, toast, money, describeFaceStep,
  checkSignupField, refreshSignup,
  copyTxt, copyAccount, copyBranch, copyAllDetails,
  toFace, startCam, captureFace, createWallet, demoLogin,
  docChosen, runKycScan, finishKyc,
  faceThen, sendTypeChange, scanToSend, doSend, doSpend, buyPick, doBuy, flipHero,
  revealMiniCard, copyCardDetails,
  addCard, toggleCardDetails, toggleFreeze, saveCardControls,
  renderCardLimits, changeCardLimit,
  secChange, setup2fa, confirm2fa, changeLimit,
  addPasskey,
};
// The markup drives everything from inline handlers. They are bound as
// real listeners rather than run inline, so the content security policy
// can refuse inline script outright - see lib/inline.ts.
Object.assign(window as unknown as Record<string, unknown>, globals);

document.addEventListener('DOMContentLoaded', () => {
  bindInline(globals);

  // Checked as it is typed, so nobody walks through a face scan to be
  // told the number they entered first was wrong.
  for (const [id, field] of [['f-name', 'name'], ['f-phone', 'phone'], ['f-said', 'said']] as const) {
    const input = $(id) as HTMLInputElement | null;
    if (!input) continue;
    input.addEventListener('input', () => { checkSignupField(field); refreshSignup(); });
    input.addEventListener('blur', () => { checkSignupField(field); refreshSignup(); });
  }
  refreshSignup();
  ($('sendTo') as HTMLInputElement | null)?.addEventListener('change', () => void lookupRecipient());
  ($('sendTo') as HTMLInputElement | null)?.addEventListener('blur', () => void lookupRecipient());
  ($('docFile') as HTMLInputElement | null)?.addEventListener('change', () => void docChosen());

  // The scanner runs only while its sheet is open. A camera that keeps
  // running behind a closed sheet drains the battery and worries people.
  const spendSheet = $('sh-spend');
  if (spendSheet) {
    new MutationObserver(() => {
      if (spendSheet.classList.contains('on')) startScanner();
      else resetScanSheet();
    }).observe(spendSheet, { attributes: true, attributeFilter: ['class'] });
  }
  const verifySheet = $('sh-faceverify');
  if (verifySheet) {
    new MutationObserver(() => {
      if (!verifySheet.classList.contains('on')) face.closeCamera();
    }).observe(verifySheet, { attributes: true, attributeFilter: ['class'] });
  }
});

/* ============================ installed app ============================== */

/**
 * Register the service worker.
 *
 * It is what makes a browser offer to install the wallet, and what lets
 * it open with no signal. Registration waits for first paint: a worker
 * competing with the first load makes the wallet slower to appear on
 * exactly the connections that need it fastest.
 *
 * Nothing it caches is anybody's money - see sw.js.
 */
function registerWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  const register = () => {
    void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {
      // Refused in a private window and on an insecure origin. The wallet
      // works without it; it just cannot be installed.
    });
  };
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });
}

/** True when the wallet is running as an installed app rather than a tab. */
function isInstalled(): boolean {
  return matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as unknown as { standalone?: boolean }).standalone === true;
}

/**
 * The shortcuts a home-screen icon offers.
 *
 * Long-pressing the installed icon offers "Receive money" and "Scan and
 * pay"; both arrive as a query on the start URL. Handled here so the
 * sheet is already open by the time the person is looking at the screen.
 */
function openFromShortcut(): void {
  const wanted = new URLSearchParams(location.search).get('open');
  if (!wanted) return;
  // Tidy the address bar either way, so a reload does not reopen it.
  history.replaceState(null, '', location.pathname);
  if (!api.token) return;
  const sheet = wanted === 'receive' ? 'sh-receive' : wanted === 'scan' ? 'sh-spend' : null;
  if (sheet) setTimeout(() => openSheet(sheet), 400);
}

/**
 * Offer to install, once, and only to somebody already using the wallet.
 *
 * Chrome hands the prompt over rather than showing its own. Firing it at
 * a stranger on the welcome screen is how install prompts earned their
 * reputation; offering it to somebody who has just been paid is worth
 * making. Ignored once, it is not raised again.
 */
let installPrompt: (Event & { prompt(): Promise<void> }) | null = null;

function watchForInstall(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    installPrompt = event as Event & { prompt(): Promise<void> };
    offerInstall();
  });
  window.addEventListener('appinstalled', () => {
    installPrompt = null;
    try { localStorage.setItem('fw2.installed', '1'); } catch { /* private browsing */ }
  });
}

function offerInstall(): void {
  if (!installPrompt || !api.token || isInstalled()) return;
  try { if (localStorage.getItem('fw2.install.declined')) return; } catch { return; }

  toast('Tap here to add FACE Wallet to your home screen.', 9000);
  const t = $('toast');
  if (!t) return;
  t.style.cursor = 'pointer';
  t.onclick = () => {
    t.onclick = null;
    const prompt = installPrompt;
    installPrompt = null;
    void prompt?.prompt();
  };
  // Not offered again if it went unanswered.
  setTimeout(() => {
    if (installPrompt) {
      try { localStorage.setItem('fw2.install.declined', '1'); } catch { /* nothing to do */ }
    }
  }, 60_000);
}

Object.assign(window as unknown as Record<string, unknown>, { isInstalled });

registerWorker();
watchSwipes();
watchHeroFlip();
watchForInstall();
void boot().then(offerInstall).then(openFromShortcut).then(preloadCard);
